import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Clock, AlertOctagon, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getBuyerAutoPayoutStatus,
  type BuyerAutoPayoutStatus,
} from "@/lib/buyer-auto-payout.functions";

type Props = {
  orderId: string;
  onConfirmDelivery: () => void;
  onReportProblem: () => void;
  confirmDisabled?: boolean;
  /** True while the buyer's confirmDeliveryAndAutoRelease call is in flight. */
  submitting?: boolean;
};

const LOCAL_TZ =
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";

/** UK-style long format: "15 July 2026 at 10:30 PM" in buyer's local tz. */
function formatLongLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const time = d
      .toLocaleTimeString("en-GB", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toUpperCase();
    return `${date} at ${time}`;
  } catch {
    return iso;
  }
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "0s";
  const totalSeconds = Math.floor(ms / 1000);
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (d >= 1) return `${d}d ${h}h ${m}m`;
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

/** Coarse label announced to screen readers (updated at most once/minute). */
function coarseRemaining(ms: number): string {
  if (ms <= 0) return "less than a minute";
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d >= 1) return `${d} day${d > 1 ? "s" : ""} ${h} hour${h === 1 ? "" : "s"}`;
  if (h >= 1) return `${h} hour${h === 1 ? "" : "s"} ${m} minute${m === 1 ? "" : "s"}`;
  return `${m} minute${m === 1 ? "" : "s"}`;
}

export function BuyerAutoPayoutPanel({
  orderId,
  onConfirmDelivery,
  onReportProblem,
  confirmDisabled,
  submitting,
}: Props) {
  const getStatus = useServerFn(getBuyerAutoPayoutStatus);

  // Track expiry-window polling: once the countdown hits zero we poll every
  // 10s for up to 5 minutes waiting for the server state to advance.
  const expiryPollStartedAt = useRef<number | null>(null);

  const { data: status, refetch, isLoading } = useQuery<BuyerAutoPayoutStatus>({
    queryKey: ["buyer-auto-payout", orderId],
    queryFn: () => getStatus({ data: { orderId } }),
    refetchInterval: (query) => {
      const s = query.state.data?.state;
      // Terminal states — stop polling.
      if (
        s === "released" ||
        s === "dispute_paused" ||
        s === "cancelled" ||
        s === "delayed" ||
        s === "not_applicable"
      ) {
        expiryPollStartedAt.current = null;
        return false;
      }
      // Delivery confirmed by buyer — background payout underway.
      if (s === "delivery_confirmed_processing") return 10_000;
      // Timer just expired — poll rapidly for up to 5 minutes.
      if (s === "processing") {
        if (expiryPollStartedAt.current === null) {
          expiryPollStartedAt.current = Date.now();
        }
        const elapsed = Date.now() - expiryPollStartedAt.current;
        return elapsed > 5 * 60_000 ? false : 10_000;
      }
      if (s === "counting_down") return 60_000;
      return false;
    },
  });

  // 1s ticker while counting down.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (status?.state !== "counting_down") return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [status?.state]);

  const remainingMs = useMemo(() => {
    if (!status?.autoPayoutEligibleAt) return null;
    const ms = new Date(status.autoPayoutEligibleAt).getTime() - Date.now();
    return ms > 0 ? ms : 0;
  }, [status?.autoPayoutEligibleAt, tick]);

  // At zero, immediately refetch so the server can advance state.
  useEffect(() => {
    if (status?.state === "counting_down" && remainingMs === 0) {
      refetch();
    }
  }, [remainingMs, status?.state, refetch]);

  // Per-minute coarse announcement for screen readers.
  const coarseLabel = useMemo(
    () => (remainingMs !== null ? coarseRemaining(remainingMs) : ""),
    // Only recompute when the whole-minute bucket changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remainingMs !== null ? Math.floor(remainingMs / 60000) : -1],
  );

  if (isLoading || !status) {
    return (
      <div className="rounded-lg border border-border/60 bg-surface-2/40 p-4 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading payout status…
      </div>
    );
  }

  if (status.state === "not_applicable" && !submitting) return null;

  // ---- In-flight buyer confirmation ---------------------------------------
  if (submitting) {
    return (
      <Panel tone="sky" role="status">
        <TitleRow icon={<Loader2 className="h-4 w-4 animate-spin" />} tone="sky">
          Delivery confirmed — releasing payment to the seller…
        </TitleRow>
        <DatesList status={status} showScheduled={false} />
      </Panel>
    );
  }

  // ---- Released ------------------------------------------------------------
  if (status.state === "released") {
    return (
      <Panel tone="emerald" role="status">
        <TitleRow icon={<CheckCircle2 className="h-4 w-4" />} tone="emerald">
          Payment released to seller
        </TitleRow>
        <p className="text-muted-foreground">The seller payout has been completed.</p>
        <DatesList status={status} showScheduled={false} />
      </Panel>
    );
  }

  // ---- Refunded / cancelled ------------------------------------------------
  if (status.state === "cancelled") {
    return (
      <Panel tone="slate" role="status">
        <TitleRow icon={<XCircle className="h-4 w-4" />} tone="slate">
          Automatic payout cancelled
        </TitleRow>
        <p className="text-muted-foreground">
          This order is no longer eligible for seller payout.
        </p>
        <DatesList status={status} showScheduled={false} />
      </Panel>
    );
  }

  // ---- Active dispute ------------------------------------------------------
  if (status.state === "dispute_paused") {
    return (
      <Panel tone="amber" role="status">
        <TitleRow icon={<AlertOctagon className="h-4 w-4" />} tone="amber">
          Automatic payout paused
        </TitleRow>
        <p className="text-muted-foreground">
          Payment will not be released while this issue is being reviewed.
        </p>
        <DatesList status={status} showScheduled={false} />
      </Panel>
    );
  }

  // ---- Delivery confirmed – processing payout ------------------------------
  if (status.state === "delivery_confirmed_processing") {
    return (
      <Panel tone="sky" role="status" ariaLive>
        <TitleRow icon={<Loader2 className="h-4 w-4 animate-spin" />} tone="sky">
          Delivery confirmed — releasing payment to the seller…
        </TitleRow>
        <DatesList status={status} showScheduled={false} />
      </Panel>
    );
  }

  // ---- 72h elapsed, payout in flight (post-expiry polling) -----------------
  if (status.state === "processing") {
    return (
      <Panel tone="sky" role="status" ariaLive>
        <TitleRow icon={<Loader2 className="h-4 w-4 animate-spin" />} tone="sky">
          Automatic payout is being processed
        </TitleRow>
        <p className="text-muted-foreground">
          The scheduled release time has passed. We are checking the payment status.
        </p>
        <DatesList status={status} showScheduled />
      </Panel>
    );
  }

  // ---- Auto-payout failed --------------------------------------------------
  if (status.state === "delayed") {
    return (
      <Panel tone="amber" role="status" ariaLive>
        <TitleRow icon={<Clock className="h-4 w-4" />} tone="amber">
          Payment processing delayed
        </TitleRow>
        <p className="text-muted-foreground">
          Delivery is confirmed, but payment processing is taking longer than expected.
          Our team has been notified.
        </p>
        <DatesList status={status} showScheduled />
      </Panel>
    );
  }

  // ---- Counting down -------------------------------------------------------
  const ms = remainingMs ?? 0;
  return (
    <div
      className="rounded-lg border border-border/60 bg-surface-2/60 p-4 text-sm space-y-3"
      role="region"
      aria-label="Automatic seller payout countdown"
    >
      <div>
        <div className="font-medium text-foreground flex items-center gap-2">
          <Clock className="h-4 w-4 text-gold" aria-hidden="true" />
          Automatic seller payout
        </div>
        <p className="mt-1 text-muted-foreground">
          If you do not report a problem, payment will be released to the seller
          automatically.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <div
          className="font-display text-lg tabular-nums text-gold break-words"
          aria-hidden="true"
        >
          Payment releases in {formatRemaining(ms)}
        </div>
        {/* Screen-reader-only, updates once per minute. */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          Payment releases in {coarseLabel}
        </span>
        {status.autoPayoutEligibleAt && (
          <div className="text-xs text-muted-foreground break-words">
            Scheduled seller payout:{" "}
            <span className="text-foreground">
              {formatLongLocal(status.autoPayoutEligibleAt)}
            </span>
            {LOCAL_TZ ? ` (${LOCAL_TZ})` : ""}
          </div>
        )}
      </div>

      <DatesList status={status} showScheduled={false} />

      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
        <Button
          size="sm"
          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white"
          onClick={onConfirmDelivery}
          disabled={confirmDisabled}
        >
          <CheckCircle2 className="h-4 w-4 mr-1" aria-hidden="true" />
          Everything is OK — confirm delivery
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="w-full sm:w-auto"
          onClick={onReportProblem}
        >
          <AlertOctagon className="h-4 w-4 mr-1" aria-hidden="true" />
          Report a problem
        </Button>
      </div>
    </div>
  );
}

// -------------------- helpers --------------------

type Tone = "sky" | "emerald" | "amber" | "slate";
const TONE_CLASSES: Record<Tone, { border: string; bg: string; title: string }> = {
  sky: {
    border: "border-sky-500/40",
    bg: "bg-sky-500/10",
    title: "text-sky-300",
  },
  emerald: {
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/10",
    title: "text-emerald-300",
  },
  amber: {
    border: "border-amber-500/40",
    bg: "bg-amber-500/10",
    title: "text-amber-300",
  },
  slate: {
    border: "border-slate-500/40",
    bg: "bg-slate-500/10",
    title: "text-slate-300",
  },
};

function Panel({
  tone,
  role,
  ariaLive,
  children,
}: {
  tone: Tone;
  role?: "status" | "region";
  ariaLive?: boolean;
  children: React.ReactNode;
}) {
  const c = TONE_CLASSES[tone];
  return (
    <div
      className={`rounded-lg border ${c.border} ${c.bg} p-4 text-sm space-y-1`}
      role={role}
      aria-live={ariaLive ? "polite" : undefined}
    >
      {children}
    </div>
  );
}

function TitleRow({
  icon,
  tone,
  children,
}: {
  icon: React.ReactNode;
  tone: Tone;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center gap-2 font-medium ${TONE_CLASSES[tone].title} break-words`}
    >
      <span aria-hidden="true" className="shrink-0">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

function DatesList({
  status,
  showScheduled,
}: {
  status: BuyerAutoPayoutStatus;
  showScheduled: boolean;
}) {
  const rows: Array<[string, string | null]> = [
    ["Delivered on", status.deliveredAt],
    showScheduled ? ["Scheduled seller payout", status.autoPayoutEligibleAt] : ["", null],
    ["Buyer confirmed delivery on", status.deliveryConfirmedAt],
    ["Payment released on", status.payoutReleasedAt],
  ].filter(([label, val]) => label && val) as Array<[string, string]>;

  if (rows.length === 0) return null;
  return (
    <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-[auto,1fr]">
      {rows.map(([label, iso]) => (
        <div key={label} className="contents">
          <dt className="sm:pr-2">{label}:</dt>
          <dd className="text-foreground break-words">{formatLongLocal(iso)}</dd>
        </div>
      ))}
    </dl>
  );
}
